import { useSyncExternalStore } from "react";

// ONE store primitive for the whole app.
//
// The measured defect is not "no store" — it is hand-rolled copies of one store (four of them, at the count
// that prompted this), each with its own subscribe/notify convention, and each with the same tearing bug: a
// `useState` + subscribe pair reads the value in an effect, so under React 18's concurrent rendering two
// components can render one commit with two different values. `useSyncExternalStore` is the API that exists precisely to make that
// unrepresentable, and it costs 0 bytes of dependency.
//
// `tests/stores.test.ts` asserts that no file outside `src/stores/` defines a subscribe+getSnapshot pair — a
// second copy of this is the defect, not the absence of a library.

/**
 * THE READ HALF OF A STORE — everything a component needs and nothing it may do.
 *
 * It exists because not every external source this app renders is a `Store<T>` that something `set`s: the
 * stitch (`stream/stitch.ts`) is an external store whose value is produced by the socket and the seed, and it
 * has no `set` for anyone to call. Without this type it would have to reach for `useSyncExternalStore`
 * itself, which is the SECOND copy of a subscription convention that `tests/stores.test.ts` exists to forbid
 * — and the tearing bug that rule guards against is real for the stitch too: two panels on one token page
 * rendering one commit with two different tapes.
 */
export interface ReadableStore<T> {
  get(): T;
  subscribe(listener: () => void): () => void;
}

export interface Store<T> extends ReadableStore<T> {
  set(next: T | ((prev: T) => T)): void;
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<() => void>();

  return {
    get: () => value,
    set(next) {
      const resolved = typeof next === "function" ? (next as (prev: T) => T)(value) : next;
      // Reference equality is the whole change test. Every store below produces a NEW object/array on change,
      // so a no-op set does not wake every subscriber — and a mutation-in-place is a bug that shows up here as
      // "the screen did not update", which is the correct place for it to show up.
      if (Object.is(resolved, value)) return;
      value = resolved;
      // Copy before iterating: a listener may unsubscribe itself (an unmounting component does exactly that).
      for (const listener of [...listeners]) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// The one way a component reads a store. `getSnapshot` and `getServerSnapshot` are the same function: this app
// is never server-rendered, and passing the same reader for both is what stops React from tearing on hydration
// paths that do not exist here but would silently return `undefined` if they did.
export function useStore<T>(store: ReadableStore<T>): T {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

// A selected read, for the case where a component depends on ONE field of a wide store and must not re-render
// on every other field. `select` MUST be referentially stable across renders (a module-level function or a
// `useCallback`) and MUST return a primitive or a stable reference — returning a fresh object every call makes
// `useSyncExternalStore` loop forever, and that failure is loud rather than silent, which is why this is a
// comment and not a runtime guard.
export function useStoreSelector<T, S>(store: ReadableStore<T>, select: (value: T) => S): S {
  const read = () => select(store.get());
  return useSyncExternalStore(store.subscribe, read, read);
}
